import { defineComponent, ref, computed, onMounted } from 'vue';
import sys from '../../system';

import { framework, accounts, tasks } from '@truckdown/jsclient/apis';

import controlPanel from '../controlPanel/controlPanel.vue'

import {
    solid
} from '@truckdown/jsclient/icons';

sys.fontLibrary.add(solid.faBars);
sys.fontLibrary.add(solid.faUserCircle);
sys.fontLibrary.add(solid.faSearch);
sys.fontLibrary.add(solid.faTimesCircle);
sys.fontLibrary.add(solid.faTasks);
sys.fontLibrary.add(solid.faIdCard);

interface ILink {
    name: string,
    title: string,
    url: string
}

interface IIMage {
    width: number,
    height: number,
    alt: string,
    src: string
}

var getLinks = function (): ILink[] {
    if (sys.framework.context.sys == 'planrv') {
        return [
            {
                name: 'Search',
                title: 'Find RV Services, Campgrounds, and Rest Areas anywhere in the US or Canada',
                url: '/'
            },
            {
                name: 'Routing',
                title: 'Create and search along routes to help plan your trip',
                url: '/route'
            },
            {
                name: 'For RVs',
                title: 'Minize your RV maintenance costs with PlanRV services',
                url: '/shop/features'
            },
            {
                name: 'Advertise',
                title: 'Advertise your business on PlanRV',
                url: '/shop/advertise'
            },
            {
                name: 'Admin',
                title: 'Access and Manage all of PlanRV\'s products & services',
                url: '/admin'
            }
        ];
    } else {
        return [
            {
                name: 'Search',
                title: 'Find heavy-duty truck services anywhere in the US or Canada',
                url: '/'
            },
            {
                name: 'Routing',
                title: 'Create truck-legal routes, view route reports, and manage your IFTA fuel tax reporting information',
                url: '/route'
            },
            {
                name: 'Fleets',
                title: 'Minimize your fleet maintenance costs with TruckDown services',
                url: '/shop/fleets'
            },
            {
                name: 'Advertise',
                title: 'Advertise your business on TruckDown',
                url: '/shop/advertise'
            },
            {
                name: 'Admin',
                title: 'Access and Manage all of TruckDown\'s products & services',
                url: '/admin'
            }
        ];
    }
}

var getLogo = function (): IIMage {
    if (sys.framework.context.sys == 'planrv') {
        return {
            width: 151,
            height: 40,
            alt: 'PlanRV',
            src: '/img/logo.planrv.png'
        };
    } else {
        return {
            width: 165,
            height: 40,
            alt: 'TruckDown',
            src: '/img/logo.truckdown.png'
        };
    }
}

var getMobileLogo = function (): IIMage {
    if (sys.framework.context.sys == 'planrv') {
        return {
            width: 40,
            height: 40,
            alt: 'PlanRV',
            src: '/img/logo.mobile.planrv.png'
        };
    } else {
        return {
            width: 40,
            height: 40,
            alt: 'TruckDown',
            src: '/img/logo.mobile.truckdown.png'
        };
    }
}

var isUserAnAdmin = function (): boolean {
    if (!sys.framework.context.isAuthenticated()) {
        return false;
    }
    return sys.framework.context.isInRole(framework.SecurityConstants.Roles.SystemRole)
        || sys.framework.context.isInRole(framework.SecurityConstants.Roles.CSRRole);
}

var getProfileImage = function (): string | null {
    if (!sys.framework.context.isAuthenticated()) {
        return null;
    }
    var profile = sys.framework.context.getSetting('profilepic');
    if (profile === null || profile === '000000000000000000000000') {
        return null;
    }

    let userData = sys.framework.getRequiredService<accounts.IUserData>('IUserData');
    return sys.framework.context.getServiceUrl('api', userData.profileImagePath(profile));
}

var getTaskCount = async function (): Promise<number> {
    if (!isUserAnAdmin()) {
        return 0;
    } else {
        let finder = sys.framework.getRequiredService<tasks.ITaskFinder>('ITaskFinder');
        let model = new tasks.FindModel();

        model.assignedTo = sys.framework.context.user?.id;
        model.statuses.push(tasks.AdminTaskStatus.New);
        model.statuses.push(tasks.AdminTaskStatus.InProgress);
        model.folder = 'Inbox';

        return await finder.getCount(model)
            .catch((err) => {
                console.log(err);
                return 0;
            });
    }
}

export default defineComponent({
    name: 'primary-header',
    props: {},
    setup: function (props, context) {
        const isPlanRV = computed<boolean>(() => sys.framework.context.sys == 'planrv');
        const isAuthenticated = ref<boolean>(sys.framework.context.isAuthenticated());
        const isAdmin = ref<boolean>(isUserAnAdmin());

        function none(evt: Event) {

        };

        //logo

        const logo = ref<IIMage>(getLogo());
        const mobileLogo = ref<IIMage>(getMobileLogo());

        //links

        const links = ref<ILink[]>(getLinks());

        //profile

        const profileImage = ref<string | null>(getProfileImage());
        const firstName = ref<string | undefined>(sys.framework.context.user?.firstName);
        const lastName = ref<string | undefined>(sys.framework.context.user?.lastName);

        //menu

        const showMenu = ref<boolean>(false);
        const hasRealUsername = ref<boolean>(
            sys.framework.context.hasClaim(framework.SecurityConstants.Claims.RealUser));

        function toggleMenu(evt: Event) {
            showMenu.value = !showMenu.value;
            if (showMenu.value) {
                closeContext(evt);
                closeControlPanel(evt);
            }
        };

        function closeMenu(evt: Event) {
            showMenu.value = false;
        };


        //context info

        const showContext = ref<boolean>(false);

        function toggleContext(evt: Event) {
            showContext.value = !showContext.value;
            if (showContext.value) {
                closeMenu(evt);
                closeControlPanel(evt);
            }
        };

        function closeContext(evt: Event) {
            showContext.value = false;
        };

        //control panel

        const showControlPanel = ref<boolean>(false);

        function toggleControlPanel(evt: Event) {
            showControlPanel.value = !showControlPanel.value;
            if (showControlPanel.value) {
                closeMenu(evt);
                closeContext(evt);

                if (searchText.value && searchText.value.length > 0) {
                    controlPanelRef.value.search(searchText.value);
                }
            }
        };

        function closeControlPanel(evt: Event) {
            showControlPanel.value = false;
        };

        const searchText = ref<string>('');
        const searchSelected = ref<boolean>(false);
        const controlPanelRef = ref<InstanceType<typeof controlPanel> | null>(null);

        function focusSearchInput(evt: Event) {
            searchSelected.value = true;
        };

        function blurSearchInput(evt: Event) {
            searchSelected.value = false;
        };

        function searchInputKeyUp(evt: Event) {
            if (!showControlPanel.value) {
                toggleControlPanel(evt);
            } else {
                console.log('already open');
                if (searchText.value && searchText.value.length > 0) {
                    console.log('searching');
                    controlPanelRef.value.search(searchText.value);
                }
            }
        };

        //tasks

        const taskCount = ref<number>(0);

        onMounted(async () => {
            taskCount.value = await getTaskCount();
        });

        //return

        return {
            isPlanRV,
            links,
            logo,
            mobileLogo,
            isAuthenticated,
            isAdmin,
            hasRealUsername,
            profileImage,
            firstName,
            lastName,
            showMenu,
            toggleMenu,
            closeMenu,
            toggleContext,
            closeContext,
            toggleControlPanel,
            closeControlPanel,
            showContext,
            showControlPanel,
            none,
            taskCount,
            searchText,
            searchInputKeyUp,
            focusSearchInput,
            blurSearchInput,
            searchSelected,
            controlPanelRef
        };
    }
});